import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { COMPANY_DOMAINS } from "@routebot/constants";
import { DemoLeadCaptureContext } from "./DemoLeadCaptureContext";
import { AuthContext } from "shared/context/auth-context";
import { useHttpClient } from "shared/hooks/http-hook";
import { trackSuiteEvent } from "shared/util/track";
import LeadCaptureModal from "./LeadCaptureModal";
import DemoBookCallButton from "./DemoBookCallButton";
import DemoOnboarding from "./onboarding/DemoOnboarding";
import DemoOptimizeCoach from "./DemoOptimizeCoach";
import DemoMobilePeek from "./DemoMobilePeek";
import RoutePhoneReveal from "./route-phones/RoutePhoneReveal";

// Onboarding sektör seçimi → backend transportType değeri.
const industryToTransportType = (industry) => {
  if (industry === "school") return COMPANY_DOMAINS.STUDENT;
  if (industry === "personnel") return COMPANY_DOMAINS.PERSONEL;
  return "both";
};

const STORAGE_KEY = "demo_lead_email_captured";

// "Demo/call istendi" ve "onboarding görüldü" bayrakları tarayıcıya değil, demo HESABINA
// bağlıdır (auth.id ile anahtarlanır). Böylece kişi yeni bir demo açtığında akış yeniden
// görünür ve reload'da da doğru kalır.
const requestedKeyFor = (id) => (id ? `demo_requested:${id}` : null);
const onboardingKeyFor = (id) => (id ? `onboarding_done:${id}` : null);

// Zamanlama: "Enjoying the demo?" (time_based) ~3 dk sonra. Optimize sonrası telefon
// paneli açıkken form çıkmaz; panel kapanınca ya da 1 dk dolunca çıkar. Çıkış-niyeti 30 sn sonra.
const TIME_TRIGGER_MS = 180 * 1000;
const EXIT_INTENT_MIN_MS = 30 * 1000;
const OPTIMIZE_SUCCESS_DELAY_MS = 60 * 1000;
// Spam olmasın: oturum başına en fazla 2 kez (kibar; kalıcı buton zaten hep orada).
const MAX_SHOWS = 2;
// İki ayrı tetik (örn. optimize_success + time_based) zamanca denk gelse bile modallar
// üst üste gelmesin: bir modal gösterildikten sonra bu süre boyunca yenisi çıkmaz.
const COOLDOWN_MS = 90 * 1000;

const DemoLeadCaptureProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const navigate = useNavigate();
  const location = useLocation();
  const onStartPage = location.pathname === "/start";
  const requestedKey = requestedKeyFor(auth?.id);
  const onboardingKey = onboardingKeyFor(auth?.id);

  const readFlag = (key) => {
    try {
      return key ? localStorage.getItem(key) === "1" : false;
    } catch {
      return false;
    }
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [modalSource, setModalSource] = useState(null);
  const [emailCaptured, setEmailCaptured] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  });
  const [requestedDemo, setRequestedDemo] = useState(() => readFlag(requestedKey));
  // Founder video and the school/personnel question stay off. A seeded demo opens on the
  // first school route. A real signup answers transport type on the dashboard instead.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [routePhones, setRoutePhones] = useState(null);
  const timerRef = useRef(null);
  const optimizeTimerRef = useRef(null);
  const shownCountRef = useRef(0);
  const visibleRef = useRef(false);
  const demoOpenedRef = useRef(false);
  const lastShownAtRef = useRef(0);

  // Hesap değişirse (yeni demo) bayrakları yeniden oku.
  useEffect(() => {
    setRequestedDemo(readFlag(requestedKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedKey]);

  // Funnel: demo açıldı (bir kez)
  useEffect(() => {
    if (!demoOpenedRef.current) {
      demoOpenedRef.current = true;
      trackSuiteEvent("Demo — Opened", "page_view", { key: "demo_opened" });
    }
  }, []);

  const markDemoRequested = useCallback(() => {
    setRequestedDemo(true);
    try {
      if (requestedKey) localStorage.setItem(requestedKey, "1");
    } catch {
      /* storage yoksa sorun değil */
    }
  }, [requestedKey]);

  const finishOnboarding = useCallback(() => {
    setShowOnboarding(false);
    try {
      if (onboardingKey) localStorage.setItem(onboardingKey, "1");
    } catch {
      /* storage yoksa sorun değil */
    }
  }, [onboardingKey]);

  useEffect(() => {
    if (onStartPage) finishOnboarding();
  }, [onStartPage, finishOnboarding]);

  // Onboarding tamamlanınca: sektöre uygun Servisler sekmesine git ve ilk servisi otomatik aç.
  // (Skip'te yönlendirme yapmayız; kullanıcı doğrudan demoda kalır.)
  const handleOnboardingComplete = useCallback(
    (industry) => {
      finishOnboarding();
      const tab = industry === "personnel" ? "standardPersonelServices" : "vehicleRoutes";
      navigate(`/services?tab=${tab}&open=first`);
    },
    [finishOnboarding, navigate],
  );

  // Sektör seçimine göre company domain + permission'larını güncelle, sonra auth'u tazele.
  // "Hazırlanıyor" ekranı bu çağrının gecikmesini maskeler.
  const handleIndustrySelect = useCallback(
    async (industry) => {
      if (!auth?.id || !auth?.token) return;
      try {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/clients/${auth.id}/transport-type`,
          "PATCH",
          JSON.stringify({ transportType: industryToTransportType(industry) }),
          { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        );
        if (auth.login) await auth.login(auth.id, auth.token);
      } catch {
        /* domain güncellenemezse demo yine "both" ile çalışır, akış bozulmaz */
      }
    },
    [auth, sendRequest],
  );

  // Onboarding sırasında VEYA demo/call istenmişse VEYA e-posta yakalanmışsa otomatik modal çıkmaz.
  const suppressed = emailCaptured || requestedDemo || showOnboarding || onStartPage;

  const showModal = useCallback(
    (source) => {
      if (suppressed) return;
      if (visibleRef.current) return;
      if (shownCountRef.current >= MAX_SHOWS) return;
      // Cooldown: bir önceki modaldan bu yana yeterli süre geçmediyse gösterme.
      if (Date.now() - lastShownAtRef.current < COOLDOWN_MS) return;
      shownCountRef.current += 1;
      lastShownAtRef.current = Date.now();
      visibleRef.current = true;
      setModalSource(source);
      setModalVisible(true);
    },
    [suppressed],
  );

  const clearOptimizeTimer = useCallback(() => {
    if (optimizeTimerRef.current) {
      clearTimeout(optimizeTimerRef.current);
      optimizeTimerRef.current = null;
    }
  }, []);

  // Telefon paneli açıkken form gelmez. Panel kapanınca hemen, açık kalırsa 1 dk sonra.
  const triggerOptimizeSuccess = useCallback((preview) => {
    if (preview?.stops?.length) setRoutePhones(preview);
    if (suppressed || shownCountRef.current >= MAX_SHOWS) return;
    clearOptimizeTimer();
    optimizeTimerRef.current = setTimeout(() => {
      optimizeTimerRef.current = null;
      setRoutePhones(null);
      showModal("optimize_success");
    }, OPTIMIZE_SUCCESS_DELAY_MS);
  }, [suppressed, showModal, clearOptimizeTimer]);

  const dismissRoutePhones = useCallback(() => {
    setRoutePhones(null);
    if (!optimizeTimerRef.current) return;
    clearOptimizeTimer();
    showModal("optimize_success");
  }, [showModal, clearOptimizeTimer]);

  const handleClose = useCallback(() => {
    visibleRef.current = false;
    setModalVisible(false);
  }, []);

  const openKeepAccount = useCallback(() => {
    clearOptimizeTimer();
    setRoutePhones(null);
    visibleRef.current = true;
    lastShownAtRef.current = Date.now();
    setModalSource("keep_account");
    setModalVisible(true);
    trackSuiteEvent("Demo — Keep Account Opened", "modal_view", { key: "keep_account_opened" });
  }, [clearOptimizeTimer]);

  const handleEmailCaptured = useCallback(() => {
    setEmailCaptured(true);
    sessionStorage.setItem(STORAGE_KEY, "true");
    visibleRef.current = false;
    setModalVisible(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    clearOptimizeTimer();
  }, [clearOptimizeTimer]);

  // Zaman tetiği + çıkış-niyeti
  useEffect(() => {
    if (suppressed) return undefined;

    timerRef.current = setTimeout(() => {
      showModal("time_based");
    }, TIME_TRIGGER_MS);

    let exitReady = false;
    const exitTimer = setTimeout(() => {
      exitReady = true;
    }, EXIT_INTENT_MIN_MS);

    const onMouseOut = (e) => {
      if (!exitReady) return;
      if (!window.matchMedia("(pointer: fine)").matches) return;
      if (e.clientY > 12) return;
      if (e.relatedTarget !== null) return;
      showModal("demo_exit_intent");
    };

    document.addEventListener("mouseout", onMouseOut);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearTimeout(exitTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [suppressed, showModal]);

  return (
    <DemoLeadCaptureContext.Provider
      value={{
        showModal,
        triggerOptimizeSuccess,
        requestedDemo,
        markDemoRequested,
        openKeepAccount,
        routePhones,
        dismissRoutePhones,
      }}
    >
      {children}
      {!onStartPage && <DemoBookCallButton />}
      {!onStartPage && <DemoMobilePeek />}
      {!onStartPage && <DemoOptimizeCoach />}
      {routePhones && (
        <RoutePhoneReveal preview={routePhones} onClose={dismissRoutePhones} onKeep={openKeepAccount} />
      )}
      <LeadCaptureModal
        show={modalVisible}
        source={modalSource}
        onClose={handleClose}
        onSuccess={handleEmailCaptured}
      />
      {showOnboarding && (
        <DemoOnboarding
          show={!onStartPage}
          onComplete={handleOnboardingComplete}
          onIndustrySelect={handleIndustrySelect}
        />
      )}
    </DemoLeadCaptureContext.Provider>
  );
};

export default DemoLeadCaptureProvider;
